/**
 * ListView - Reusable drag and drop list component
 */
class ListView {
	/**
	 * @param {Object} options
	 * @param {string} options.containerId - ID of the container element
	 * @param {string} options.itemClass - CSS class for list items (default: 'list-item')
	 * @param {string} options.dragOverClass - CSS class for drag over state (default: 'drag-over')
	 * @param {Function} options.onReorder - Callback when items are reordered (receives array of item elements)
	 * @param {Function} options.updateItemNumbers - Custom function to update item numbers after reorder
	 */
	constructor(options = {}) {
		this.containerId = options.containerId;
		this.itemClass = options.itemClass || 'list-item';
		this.dragOverClass = options.dragOverClass || 'drag-over';
		this.onReorder = options.onReorder || null;
		this.updateItemNumbers = options.updateItemNumbers || null;

		this.container = null;
		this.draggedItem = null;

		this.init();
	}

	/**
	 * Initialize the list view
	 */
	init() {
		this.container = document.getElementById(this.containerId);
		if (!this.container) return;

		this.bindEvents();
	}

	/**
	 * Bind all drag and drop events
	 */
	bindEvents() {
		this.container.addEventListener('dragstart', this.handleDragStart.bind(this));
		this.container.addEventListener('dragend', this.handleDragEnd.bind(this));
		this.container.addEventListener('dragover', this.handleDragOver.bind(this));
		this.container.addEventListener('dragleave', this.handleDragLeave.bind(this));
		this.container.addEventListener('drop', this.handleDrop.bind(this));
	}

	/**
	 * Handle dragstart event
	 */
	handleDragStart(e) {
		const item = e.target.closest(`.${this.itemClass}`);
		if (!item) return;

		this.draggedItem = item;
		e.dataTransfer.effectAllowed = 'move';
		e.dataTransfer.setData('text/plain', '');
	}

	/**
	 * Handle dragend event
	 */
	handleDragEnd() {
		this.draggedItem = null;

		// Remove all drag-over classes
		this.container.querySelectorAll(`.${this.itemClass}`).forEach((item) => {
			item.classList.remove(this.dragOverClass);
		});
	}

	/**
	 * Handle dragover event
	 */
	handleDragOver(e) {
		e.preventDefault();
		e.dataTransfer.dropEffect = 'move';

		const item = e.target.closest(`.${this.itemClass}`);
		if (!item || item === this.draggedItem) return;

		// Remove drag-over from all items
		this.container.querySelectorAll(`.${this.itemClass}`).forEach((el) => {
			el.classList.remove(this.dragOverClass);
		});

		// Add drag-over to current item
		item.classList.add(this.dragOverClass);
	}

	/**
	 * Handle dragleave event
	 */
	handleDragLeave(e) {
		const item = e.target.closest(`.${this.itemClass}`);
		if (item) {
			item.classList.remove(this.dragOverClass);
		}
	}

	/**
	 * Handle drop event
	 */
	handleDrop(e) {
		e.preventDefault();

		const targetItem = e.target.closest(`.${this.itemClass}`);
		if (!targetItem || !this.draggedItem || targetItem === this.draggedItem) return;

		// Get bounding rect to determine drop position
		const rect = targetItem.getBoundingClientRect();
		const midY = rect.top + rect.height / 2;

		// Insert before or after based on mouse position
		if (e.clientY < midY) {
			this.container.insertBefore(this.draggedItem, targetItem);
		} else {
			this.container.insertBefore(this.draggedItem, targetItem.nextSibling);
		}

		// Remove drag-over class
		targetItem.classList.remove(this.dragOverClass);

		// Update item numbers if callback provided
		if (typeof this.updateItemNumbers === 'function') {
			this.updateItemNumbers(this.getItems());
		}

		// Call onReorder callback if provided
		if (typeof this.onReorder === 'function') {
			this.onReorder(this.getItems());
		}
	}

	/**
	 * Get all items in the list
	 * @returns {NodeList}
	 */
	getItems() {
		return this.container.querySelectorAll(`.${this.itemClass}`);
	}

	/**
	 * Get array of item IDs (from data-id or data-*-id attribute)
	 * @param {string} idAttribute - Data attribute name (default: 'id')
	 * @returns {Array}
	 */
	getItemIds(idAttribute = 'id') {
		return Array.from(this.getItems()).map((item) => {
			// Try data-id first, then data-*-id pattern
			return item.dataset[idAttribute] || item.dataset.id || null;
		});
	}

	/**
	 * Destroy the list view and remove event listeners
	 */
	destroy() {
		if (!this.container) return;

		this.container.removeEventListener('dragstart', this.handleDragStart);
		this.container.removeEventListener('dragend', this.handleDragEnd);
		this.container.removeEventListener('dragover', this.handleDragOver);
		this.container.removeEventListener('dragleave', this.handleDragLeave);
		this.container.removeEventListener('drop', this.handleDrop);
	}
}

export default ListView;
